import type {ReactElement, RefObject} from 'react';
import {useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './template-filters-scroll-button.module.scss';
import throttle from 'lodash/throttle';
import {SCROLL_EDGE_TOLERANCE_PX, SCROLL_ICON_OVERLAY_PX} from './template-filters-scroll-button.types';

interface TemplateFiltersScrollButtonProps {
  containerRef: RefObject<HTMLUListElement | null>;
  // When true the scroll arrows have no white fade background, so the row content stays visible behind them.
  transparentBackground?: boolean;
  iconClassName?: string;
  iconSize?: IconSize;
  iconType?: IconType;
  containerClassName?: string;
}

function getScrollStep(container: HTMLUListElement): number {
  return Math.max(container.clientWidth - SCROLL_ICON_OVERLAY_PX * 2, 0);
}

export function TemplateFiltersScrollButton({...props}: TemplateFiltersScrollButtonProps): ReactElement {
  const [showLeftIcon, setShowLeftIcon] = useState(false);
  const [showRightIcon, setShowRightIcon] = useState(false);

  useEffect(() => {
    const container = props.containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = throttle((): void => {
      setShowLeftIcon(container.scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
      const totalPossibleScroll = container.scrollWidth - container.clientWidth;
      setShowRightIcon(container.scrollLeft + SCROLL_EDGE_TOLERANCE_PX <= totalPossibleScroll);
    });

    container.addEventListener('scroll', handleScroll);

    // Recompute on layout/size and content changes so the arrows reflect actual overflow — otherwise the right
    // arrow would show even when the filters fit (e.g. the image grid's active-tab row) until the user scrolls.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((): void => {
            handleScroll();
          })
        : undefined;
    resizeObserver?.observe(container);
    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver((): void => {
            handleScroll();
          })
        : undefined;
    mutationObserver?.observe(container, {childList: true, subtree: true});

    handleScroll();

    return (): void => {
      handleScroll.cancel();
      container.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  const onClickLeftIcon = (): void => {
    const container = props.containerRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({
      left: -getScrollStep(container),
      behavior: 'smooth',
    });
  };

  const onClickRightIcon = (): void => {
    const container = props.containerRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({
      left: getScrollStep(container),
      behavior: 'smooth',
    });
  };

  const backgroundClass = props.transparentBackground ? styles.transparent : '';
  const containerClass = props.containerClassName ?? '';
  const iconClass = `${styles.icon} ${props.iconClassName ?? ''}`;
  const iconSize = props.iconSize ?? IconSize.SIZE_ICON_16;

  const getLeftScrollIcon = (): ReactElement | null => {
    return (
      <div className={`${styles.container} ${styles.left} ${backgroundClass} ${containerClass}`}>
        <Icon onClick={onClickLeftIcon} className={iconClass} icon="icon-caret-left" type={props.iconType ?? IconType.SHADOW} shape={IconShape.CIRCLE} size={iconSize} />
      </div>
    );
  };

  const getRightScrollIcon = (): ReactElement | null => {
    return (
      <div className={`${styles.container} ${styles.right} ${backgroundClass} ${containerClass}`}>
        <Icon onClick={onClickRightIcon} className={iconClass} icon="icon-caret-right" type={props.iconType ?? IconType.SECONDARY} shape={IconShape.CIRCLE} size={iconSize} />
      </div>
    );
  };
  return (
    <>
      {showLeftIcon ? getLeftScrollIcon() : null}
      {showRightIcon ? getRightScrollIcon() : null}
    </>
  );
}
